import React, { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { FaTimes, FaStore, FaEnvelope } from "react-icons/fa";
import { useDispatch } from "react-redux";
import axios from "axios";
// import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from "jwt-decode";
import OTPModal from "../../components/OtpModal";
import EmailLoginModal from "../../components/EmailLoginModal";
import Config from "../../Config";
import { Save_User } from "../../Redux/actions";
import { useNavigate } from "react-router-dom";

// Email validation
const emailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginModal = ({
  isOpen,
  onClose,
  onSwitchToSignup,
  onSwitchToLogin,
  onLoginSuccess,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Email input
  const [email, setEmail] = useState("");

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [showEmailPasswordModal, setShowEmailPasswordModal] = useState(false);
  const [showEmailOTPModal, setShowEmailOTPModal] = useState(true);
  const [loginMethod, setLoginMethod] = useState("emailOTP");
  const [otpSent, setOtpSent] = useState(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setError("");
      setShowOTPModal(false);
      setShowEmailPasswordModal(false);
      setShowEmailOTPModal(true);
      setLoginMethod("emailOTP");
      setOtpSent(false);
    }
  }, [isOpen]);

  // Email validation
  const validateEmail = (email) => {
    return emailFormat.test(email.trim());
  };

  // Send OTP to user's email address
  const handleSendOTP = async () => {
    setError("");

    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      // console.log("Sending OTP to:", email.trim());

      // API call to send OTP
      // const response = await axios.post(`https://kuality.co.in/api/auth/send-otp`, {
      const response = await axios.post(`${Config.apiUrl}/auth/send-otp`, {
        email: email.trim(),
      });

      if (response.data && response.data.success) {
        setOtpSent(true);
        setShowEmailOTPModal(false);
        setShowOTPModal(true);
      } else {
        setError(
          response.data?.message || "Failed to send OTP. Please try again."
        );
      }
    } catch (error) {
      console.error("Failed to send OTP:", error);
      setError(
        error.response?.data?.message || "Failed to send OTP. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Switch to email login with password from email login with otp
  const handleSwitchToEmailPassword = () => {
    setError(""); // Clear any existing errors when switching login methods
    setShowEmailOTPModal(false);
    setShowEmailPasswordModal(true);
    setLoginMethod("emailPassword");
  };

  const handleOTPVerify = async (otp) => {
    try {
      // API call to verify OTP
      const response = await axios.post(`${Config.apiUrl}/auth/verify-otp`, {
        email: email.trim(),
        otp: otp,
      });

      if (response.data && response.data.success) {
        // Save user data to Redux store
        const userData = {
          ...response.data.data.user,
          token: response.data.data.token,
        };

        dispatch(Save_User(userData));

        if (userData.role === Config.userType.SELLER) {
          navigate("/admin");
          // navigate to seller dashboard if the user is seller
        } else {
          navigate("/"); // navigate to home
        }

        setShowOTPModal(false);
        setShowEmailOTPModal(true); // Reset to email OTP modal for next time
        onLoginSuccess && onLoginSuccess(userData);
        onClose();
      } else {
        throw new Error(response.data?.message || "Invalid OTP");
      }
    } catch (error) {
      console.error("OTP verification failed:", error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "OTP verification failed"
      );
      throw new Error(
        error.response?.data?.message || "OTP verification failed"
      );
    }
  };

  const handleOTPClose = () => {
    setShowOTPModal(false);
    setShowEmailOTPModal(true); // back to email OTP login when OTP modal is closed
  };

  const handleResendOTP = async () => {
    try {
      setError("");

      // API call to resend OTP
      const response = await axios.post(`${Config.apiUrl}/auth/send-otp`, {
        email: email.trim(),
      });

      if (response.data && response.data.success) {
        return true; // Success
      } else {
        throw new Error(response.data?.message || "Failed to resend OTP");
      }
    } catch (error) {
      console.error("Failed to resend OTP:", error);
      // Set error in parent component
      setError(
        error.response?.data?.message || error.message || "Failed to resend OTP"
      );
      throw new Error(error.response?.data?.message || "Failed to resend OTP");
    }
  };

  const handleEmailPasswordModalClose = () => {
    setShowEmailPasswordModal(false);
  };

  const handleSwitchToEmailOTP = () => {
    // switch back to email OTP login
    setError("");
    setShowEmailPasswordModal(false);
    setShowEmailOTPModal(true);
    setLoginMethod("emailOTP");
  };

  if (!isOpen) return null;

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    // Real-time email validation
    if (value && !validateEmail(value)) {
      setError("Please enter a valid email address");
    } else {
      setError(""); // Clear error when email is valid or empty
    }
  };

  return (
    <>
      {/* Email OTP Login Modal */}
      {showEmailOTPModal && (
        <ModalOverlay>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={onClose}>
              <FaTimes className="w-4 h-4" />
            </CloseButton>

            <ModalContent>
              <Title>Login with OTP</Title>

              <EmailSection>
                <EmailInputContainer $hasError={!!error && email}>
                  <IconWrapper>
                    <FaEnvelope />
                  </IconWrapper>
                  <EmailInput
                    type="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={email}
                    autoComplete="username"
                    onChange={handleEmailChange}
                    disabled={isLoading}
                    $hasError={!!error && email}
                  />
                </EmailInputContainer>
                {error && email && <ErrorText>{error}</ErrorText>}
              </EmailSection>

              <SendOTPButton
                onClick={handleSendOTP}
                disabled={isLoading || !email.trim() || !validateEmail(email)}
              >
                {isLoading ? "Sending..." : "Send One Time Password"}
              </SendOTPButton>

              <Divider>
                <DividerLine />
                <DividerText>or</DividerText>
                <DividerLine />
              </Divider>

              {/* Switch to Login */}
              <SwitchText>
                {/* Already have an account?{" "} */}
                <SwitchLink onClick={handleSwitchToEmailPassword}>
                  Password Login
                </SwitchLink>
                <div className="text-kuality-blue select-none">|</div>
              {/* </SwitchText> */}

              {/* <SwitchText> */}
                {/* New to our website?{" "} */}
                <SwitchLink onClick={onSwitchToSignup}>
                  Create account
                </SwitchLink>
              </SwitchText>
              <SellerLink 
                href={Config.sellerDomain} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <FaStore className="text-lg" />
                <span>Become a Supplier</span>
              </SellerLink>
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* OTP Modal */}
      <OTPModal
        isOpen={showOTPModal}
        onClose={handleOTPClose}
        onVerify={handleOTPVerify}
        email={email.trim()}
        resendOtp={handleResendOTP}
      />

      {/* Email Login with Password Modal */}
      <EmailLoginModal
        isOpen={showEmailPasswordModal}
        onClose={handleEmailPasswordModalClose}
        onSwitchToSignup={onSwitchToSignup}
        onSwitchToLogin={onSwitchToLogin}
        onSwitchToEmailLogin={handleSwitchToEmailOTP}
        onLoginSuccess={onLoginSuccess}
      />
    </>
  );
};

// Styled Components
const ModalOverlay = tw.div`
  fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-md
`;

const ModalContainer = tw.div`
  bg-white rounded-lg max-w-sm w-full max-h-[90vh] overflow-y-auto relative
`;

const CloseButton = tw.button`
  absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10 transition-colors
`;

const ModalContent = tw.div`
  p-8 pt-12
`;

const Title = tw.h2`
  text-2xl font-semibold text-kuality-blue mb-6
`;

const ErrorMessage = tw.div`
  bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm
`;

const EmailSection = tw.div`
  mb-4
`;

const EmailInputContainer = tw.div`
  flex items-center border rounded-lg overflow-hidden transition-colors
  ${(props) =>
    props.$hasError
      ? "border-red-500 focus-within:border-red-600"
      : "border-gray-300 focus-within:border-kuality-blue"}
`;

const IconWrapper = tw.div`
  pl-4 pr-2 text-kuality-blue
`;

const EmailInput = tw.input`
  flex-1 px-2 py-3 outline-none text-gray-700 placeholder-gray-400 disabled:bg-gray-50 disabled:text-gray-500 transition-colors
`;

const ErrorText = tw.span`
  text-red-500 text-xs mt-1 block
`;

const SendOTPButton = tw.button`
  w-full bg-kuality-blue hover:bg-kuality-dark disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-colors duration-200 my-3
`;

const Divider = tw.div`
  flex items-center my-1
`;

const DividerLine = tw.div`
  flex-1 h-px bg-gray-300
`;

const DividerText = tw.span`
  px-4 text-gray-500 text-sm
`;

// const SwitchText = tw.p`
//   text-center text-gray-600 text-sm my-0.5
// `;
const SwitchText = tw.p`
  flex items-center justify-center space-x-1 text-sm
`;

const SwitchLink = tw.span`
  text-kuality-blue hover:text-kuality-dark cursor-pointer font-medium transition-colors
`;

const SellerLink = tw.a`
  w-full flex items-center justify-center space-x-2 border border-kuality-blue text-kuality-blue py-3 rounded-lg font-medium transition-colors duration-200 mt-2 cursor-pointer no-underline
`;

export default LoginModal;